import { Tracker } from '../tracker/tracker.js';
import { SkeletonEvent } from '../types.js';
import { NOOP, getAllMfData, getMfAssets, MF_TEST_VERSION_SEPARATOR } from './helpers/index.js';
import { shouldUseLocal, extractLocalMFs } from './localDev/index.js';
import { ManifestHandler } from './mf/manifestHandler.js';
import { overrideAssetsVersionIfNeeded } from './helpers/assetDirUtils.js';
import GlobalSharedDependenciesManager from './SharedDependenciesManager/GlobalSharedDependenciesManager.js';
import Mf from './mf/mf.js';
import CanaryManager from './CanaryManager/canaryManager.js';
import { getMFTestVersionFromURLParams } from './helpers/queryHelper.js';

class Skeleton {
    constructor({ api, mfUrl, preloadedMFs = {}, isDev = false, mfTestVersion = getMFTestVersionFromURLParams(), fallbackResourceFunction = NOOP, onEvent = NOOP, host, isAssetsFlattened = false, allMfData, mfAssets, shouldUseMfAssetsEndpoint = false, useMfAssetsFallback = false, }) {
        this.canaryManager = new CanaryManager();
        this.emitEvent = (type, data) => {
            this.tracker.track(type, data);
            this.onEvent(type, data);
        };
        this.renderMF = async (mfName, element, options = {}) => {
            const mf = this.mfs.get(mfName);
            if (!mf) {
                console.warn(`Tried to render a non-existing MF: ${mfName}`);
                return;
            }
            mf.render(element, options);
        };
        this.ensureMfInstance = async (mfName) => {
            if (!this.mfs.has(mfName)) {
                // the mfs' data might not be loaded yet - ensure initialization before continuing
                await this.initializeMfsPromise;
            }
            if (!this.mfs.has(mfName)) {
                // The data is STILL missing. When using a local setup for an MF that was not deployed yet,
                // it's possible that the MF still does not exist
                const assetsDir = this.getLocalMfAssetsDir(mfName);
                const mf = new Mf({
                    name: mfName,
                    skeletonContext: this.context,
                    initConfig: { type: 'withAssetsDir', assetsDir },
                });
                this.mfs.set(mfName, mf);
            }
        };
        this.preFetchMF = async (mfName) => {
            this.emitEvent(SkeletonEvent.PrefetchMF, { mfName });
            await this.ensureMfInstance(mfName);
            return this.mfs.get(mfName).preFetch();
        };
        this.initMF = async (mfName, appendScript) => {
            this.emitEvent(SkeletonEvent.InitMF, { mfName, initializedMFs: [...this.mfs.keys()] });
            const timeStart = Date.now();
            await this.ensureMfInstance(mfName);
            return this.mfs.get(mfName).initOnce(timeStart, appendScript);
        };
        this.handleManifestFiles = async (mfName, manifest, mfResource, { timeStart, }) => {
            const mf = this.mfs.get(mfName);
            if (!mf) {
                console.warn(`Tried to handle manifest files for a non-existing MF: ${mfName}`);
                return;
            }
            await mf.handleManifestFiles({ manifest, assetsDir: mfResource, timeStart });
        };
        this.mfUrl = mfUrl;
        this.allMfData = allMfData;
        this.isAssetsFlattened = isAssetsFlattened;
        this.onEvent = onEvent;
        const emitSkeletonEvent = this.emitEvent.bind(this);
        this.globalSharedDependencies = new GlobalSharedDependenciesManager();
        this.mfs = new Map();
        const manifestHandler = new ManifestHandler({
            emitSkeletonEvent,
            sharedDependencies: this.globalSharedDependencies,
        });
        this.context = {
            skeleton: this,
            isDev,
            fallbackResourceFunction,
            emitSkeletonEvent,
            mfApi: api,
            manifestHandler,
        };
        this.tracker = new Tracker({ host, api, preloadedMFs: new Set(Object.keys(preloadedMFs)) });
        this.emitEvent(SkeletonEvent.SkeletonCreated, { preloaded: preloadedMFs });
        this.mfTestVersion = parseTestVersion(mfTestVersion);
        this.handlePreloadedMfs(preloadedMFs); // initialize the preloaded MFs before start fetching the MF data, to prevent race conditions
        this.initializeMfsPromise = this.fetchMfsData({
            shouldUseMfAssetsEndpoint,
            mfAssets,
            allMfData,
            useMfAssetsFallback,
        });
    }
    async fetchMfsData({ shouldUseMfAssetsEndpoint, mfAssets, allMfData, useMfAssetsFallback = false, }) {
        return shouldUseMfAssetsEndpoint
            ? this.fetchCurrentMfsWithAssets(mfAssets, useMfAssetsFallback)
            : this.fetchCurrentMfs(allMfData);
    }
    async fetchCurrentMfs(allMfData) {
        const { mfs, globalSharedDependencies } = this;
        const { metaData, sharedDependencyLocations } = allMfData ||
            (await getAllMfData({
                testOverridesQueryParams: this.testOverridesQueryParams,
                mfUrl: this.mfUrl,
                isAssetsFlattened: this.isAssetsFlattened,
            }));
        Object.entries(metaData).forEach(([name, currentMetadata]) => {
            const isInCanaryExperimentalGroup = this.canaryManager.isInCanary(name, currentMetadata.canary);
            const isMfInCanary = !!currentMetadata.canary;
            const metadata = isInCanaryExperimentalGroup ? currentMetadata.canary : currentMetadata;
            if (mfs.has(name)) {
                return;
            }
            const assetsDirRaw = this.isAssetsFlattened && metadata.assetsPath ? metadata.assetsPath : metadata.path;
            const { assetsDir, manifestPath } = overrideAssetsVersionIfNeeded({
                mfName: name,
                assetsDir: assetsDirRaw,
                manifestPath: this.isAssetsFlattened ? metadata.manifestPath : undefined,
            });
            const mf = new Mf({
                name,
                initConfig: { type: 'withAssetsDir', assetsDir, manifestPath },
                mfCanary: { isInCanaryExperimentalGroup, isMfInCanary },
                version: metadata.version,
                skeletonContext: this.context,
            });
            mfs.set(name, mf);
        });
        const dependencyDetails = Object.entries(sharedDependencyLocations).map(([globalName, url]) => ({
            globalName,
            url,
        }));
        globalSharedDependencies.addKnownDependencies(dependencyDetails);
    }
    async fetchCurrentMfsWithAssets(mfAssets, useMfAssetsFallback) {
        const { mfs, globalSharedDependencies } = this;
        const { assets, sharedDependencyLocations } = mfAssets ||
            (await getMfAssets({
                testOverridesQueryParams: this.testOverridesQueryParams,
                mfUrl: this.mfUrl,
                useMfAssetsFallback,
                emitSkeletonEvent: this.emitEvent,
            }));
        Object.entries(assets).forEach(([name, currentMetadata]) => {
            if (mfs.has(name)) {
                return;
            }
            const isInCanaryExperimentalGroup = this.canaryManager.isInCanary(name, currentMetadata.canary);
            const isMfInCanary = !!currentMetadata.canary;
            const metadata = isInCanaryExperimentalGroup ? currentMetadata.canary : currentMetadata;
            if (shouldUseLocal(name)) {
                const assetsDirRaw = metadata.url;
                const { assetsDir, manifestPath } = overrideAssetsVersionIfNeeded({
                    mfName: name,
                    assetsDir: assetsDirRaw,
                    manifestPath: this.isAssetsFlattened ? metadata.url : undefined,
                });
                const mf = new Mf({
                    name,
                    initConfig: { type: 'withAssetsDir', assetsDir, manifestPath },
                    mfCanary: { isInCanaryExperimentalGroup, isMfInCanary },
                    version: metadata.version,
                    skeletonContext: this.context,
                });
                mfs.set(name, mf);
                return;
            }
            const mf = new Mf({
                name,
                initConfig: {
                    type: 'withAssetsData',
                    assetsDir: metadata.url,
                    manifest: metadata,
                },
                mfCanary: { isInCanaryExperimentalGroup, isMfInCanary },
                version: metadata.version,
                skeletonContext: this.context,
            });
            mfs.set(name, mf);
        });
        const dependencyDetails = Object.entries(sharedDependencyLocations).map(([globalName, url]) => ({
            globalName,
            url,
        }));
        globalSharedDependencies.addKnownDependencies(dependencyDetails);
    }
    handlePreloadedMfs(preloadedMFs) {
        Object.entries(preloadedMFs)
            .filter(([name]) => !shouldUseLocal(name))
            .forEach(([name, entrypoints]) => {
            const mf = new Mf({ name, skeletonContext: this.context, initConfig: { type: 'preloaded', entrypoints } });
            this.mfs.set(name, mf);
        });
    }
    getLocalMfAssetsDir(mfName) {
        // When using a local setup for an MF that was not deployed yet, it's possible that the MF still does not exist
        const { endpoint, } = shouldUseLocal(mfName) ? extractLocalMFs()[mfName] || {} : {};
        if (!endpoint) {
            this.emitEvent(SkeletonEvent.MFNotInList, { mfName });
            throw new Error(`'${mfName}' does not exist in Micro frontends list`);
        }
        return endpoint;
    }
    get testOverridesQueryParams() {
        if (!this.mfTestVersion) {
            return undefined;
        }
        const { mfName, version, testEnvOverride } = this.mfTestVersion;
        const override = `${mfName}~${version}`;
        const testEnvOverrideParam = testEnvOverride ? `~${testEnvOverride}` : '';
        return `${override}${testEnvOverrideParam}`;
    }
}
function parseTestVersion(mfTestVersion) {
    if (!mfTestVersion || ['null', 'null~undefined'].includes(mfTestVersion)) {
        return undefined;
    }
    // overriding with test version i.e. "https://microfrontends.monday.com/Docs/10" => "https://microfrontends.monday.com/tests/Docs/3"
    const [mfName, version, testEnvOverride] = mfTestVersion.split(MF_TEST_VERSION_SEPARATOR);
    return { mfName, version, testEnvOverride };
}

export { Skeleton as default };
